import { getSessionOpenUrl } from '@/lib/gift-agent';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type {
  GuestAccessInfo,
  GuestSessionSummary,
} from '@claude-code-api/shared';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ExternalLink } from 'lucide-react';
import { SharedSessionAccessGate } from '@/components/shared/SharedSessionAccessGate';
import { useTranslation } from 'react-i18next';

export default function SharedSessionPage() {
  const { t } = useTranslation('session');
  const { sessionId } = useParams<{ sessionId: string }>();
  const [session, setSession] = useState<GuestSessionSummary | null>(null);
  const [accessInfo, setAccessInfo] = useState<GuestAccessInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [authorizing, setAuthorizing] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);

  const fetchSharedSession = useCallback(async () => {
    if (!sessionId) return false;

    const sessionResponse = await fetch(`/api/sessions/${sessionId}/share`, {
      credentials: 'include',
    });
    if (!sessionResponse.ok) {
      setSession(null);
      setAccessInfo(null);
      return false;
    }

    const sessionData = await sessionResponse.json();
    setSession(sessionData.data);

    const accessResponse = await fetch(
      `/api/sessions/${sessionId}/share/access`,
      { credentials: 'include' },
    );
    if (accessResponse.ok) {
      const accessData = await accessResponse.json();
      setAccessInfo(accessData.data);
    } else {
      setAccessInfo(null);
    }

    return true;
  }, [sessionId]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const ok = await fetchSharedSession();
        if (cancelled) return;
        if (!ok) {
          setSession(null);
        }
      } catch {
        if (!cancelled) {
          setSession(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [fetchSharedSession]);

  const verifyAccess = async (password: string) => {
    if (!sessionId) return;
    setAuthorizing(true);
    setAccessError(null);
    try {
      const response = await fetch(`/api/sessions/${sessionId}/share/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        console.error('Shared session access denied:', data?.error);
        setAccessError(t('errors.accessDenied'));
        return;
      }

      const ok = await fetchSharedSession();
      if (!ok) {
        setAccessError(t('errors.sharedUnavailable'));
      }
    } catch {
      setAccessError(t('errors.verifyPassword'));
    } finally {
      setAuthorizing(false);
    }
  };

  const formatDate = (date: string | Date) =>
    new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-6 space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 rounded-lg" />
      </div>
    );
  }

  if (!session || !accessInfo) {
    return (
      <SharedSessionAccessGate
        title="Shared session access"
        description="Enter the shared session password to open this container."
        loading={authorizing}
        error={accessError}
        onSubmit={verifyAccess}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold font-heading">
              {session.projectName || 'Shared session'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Shared access to this live container.
            </p>
          </div>
          {session.runnerType === 'acpx' ||
          session.runnerType === 'acpx-experimental' ||
          session.runnerType === 'acpx-team' ? (
            <Button
              onClick={() => {
                window.open(
                  getSessionOpenUrl(session.id, { shared: true }),
                  '_blank',
                );
              }}
              className="gap-1.5"
            >
              <ExternalLink className="w-4 h-4" />
              Open Agent
            </Button>
          ) : (
            <Button
              onClick={() =>
                window.open(
                  getSessionOpenUrl(session.id, { shared: true }),
                  '_blank',
                )
              }
              className="gap-1.5"
            >
              <ExternalLink className="w-4 h-4" />
              Open Session
            </Button>
          )}
        </div>

        <div className="rounded-lg border bg-card p-6 space-y-4">
          <h2 className="text-lg font-semibold font-heading">Access</h2>
          {accessInfo.previewUrls.port5173 ? (
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Preview</p>
                <p className="font-mono text-xs break-all">
                  {accessInfo.previewUrls.port5173}
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() =>
                  window.open(accessInfo.previewUrls.port5173!, '_blank')
                }
              >
                Open preview
              </Button>
            </div>
          ) : null}

          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Editor</p>
              <p className="font-mono text-xs break-all">
                {accessInfo.codeServerUrl}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() =>
                window.open(
                  getSessionOpenUrl(session.id, { shared: true }),
                  '_blank',
                )
              }
            >
              Open editor
            </Button>
          </div>
        </div>

        <div className="rounded-lg border bg-card p-6">
          <h2 className="text-lg font-semibold font-heading mb-4">
            Session details
          </h2>
          <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-3">
            <span className="text-sm text-muted-foreground">Runner</span>
            <span className="text-sm">
              <Badge variant="secondary">{session.runnerType}</Badge>
            </span>
            <span className="text-sm text-muted-foreground">Resources</span>
            <span className="text-sm">
              {session.cpuCores} cores / {session.memoryGb} GB
            </span>
            <span className="text-sm text-muted-foreground">Created</span>
            <span className="text-sm">{formatDate(session.createdAt)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
